import * as Yup from 'yup';

import { IntegrationType } from '@core/types/api/generated-enums';
import { ExcludeMethods } from '@core/types/generics';

import Enums from '../types/api/Enums';
import {
  type Integration,
  type IntegrationWireFormat,
  NewIntegration,
} from '../types/api/Integration/Integration';
import { NIL_UUID } from '../utils/uuid';
import { YupBoolean, YupString } from '../utils/yupHelpers';

export type IntegrationFormikType = Omit<
  IntegrationWireFormat,
  | 'config'
  | 'createdAt'
  | 'createdBy'
  | 'updatedAt'
  | 'updatedBy'
  | 'archivedAt'
  | 'archivedBy'
  | 'isArchived'
> & {
  config: string;
};

export const IntegrationSchema = Yup.object().shape({
  name: YupString('Name'),
  integrationType: YupString('Integration Type'),
  externalIdentifier: YupString('External Identifier'),
  isActive: YupBoolean('Is Active'),
  config: Yup.string()
    .label('Configuration')
    .default('')
    .required('Configuration is required')
    .test('is-json', 'Configuration must be valid JSON', (value) => {
      if (value === '' || value === null || value === undefined) return false;
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    }),
});

export const integrationFormik = (
  integration: Partial<Integration> | undefined | null,
): IntegrationFormikType => {
  // Convert config to string for editing
  const configString = JSON.stringify(integration?.config ?? {}, null, 2);

  return {
    id: integration?.id ?? NIL_UUID,
    tenantId: integration?.tenantId ?? NIL_UUID,
    name: integration?.name ?? '',
    integrationType: integration?.integrationType ?? Enums.IntegrationType.Series,
    config: configString,
    externalIdentifier: integration?.externalIdentifier ?? '',
    isActive: integration?.isActive ?? true,
    exportQuotes: integration?.exportQuotes ?? false,
  };
};

export const formikIntegrationToWire = (formikIntegration: IntegrationFormikType): Integration => {
  // Define the data with an explicit type annotation separately from `NewIntegration()`,
  // so that the TypeScript compiler will error if/when we forget to update this when
  // adding new non-metadata properties.
  const integration: ExcludeMethods<
    Omit<
      Integration,
      | 'createdAt'
      | 'createdBy'
      | 'updatedAt'
      | 'updatedBy'
      | 'isArchived'
      | 'archivedAt'
      | 'archivedBy'
    >
  > = {
    id: formikIntegration.id,
    tenantId: formikIntegration.tenantId,
    name: formikIntegration.name,
    integrationType: formikIntegration.integrationType as IntegrationType,
    config: JSON.parse(formikIntegration.config),
    externalIdentifier: formikIntegration.externalIdentifier,
    isActive: formikIntegration.isActive,
    exportQuotes: formikIntegration.exportQuotes,
  };
  return NewIntegration(integration);
};
